import type { ReportBin } from "@/lib/data/monthly-report/data";

/**
 * Number formatting for the report pages. Server components build it once per
 * render; client components receive pre-formatted strings instead, so Intl
 * never runs twice with different ICU data.
 */
export const createReportFormat = (locale: string) => {
  const money = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
  const money1 = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const plain = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const share = new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
  });
  const signed = new Intl.NumberFormat(locale, {
    style: "percent",
    signDisplay: "exceptZero",
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  });
  const whole = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });

  return {
    money: (value: number) => money.format(value),
    /** Euro per m², one decimal. */
    perM2: (value: number) => `${money1.format(value)}/m²`,
    size: (value: number) => `${whole.format(value)} m²`,
    number: (value: number) => plain.format(value),
    whole: (value: number) => whole.format(value),
    /** 0.234 -> "23%", with "<1%" instead of a misleading "0%". */
    share: (value: number) =>
      value > 0 && value < 0.005
        ? `<${share.format(0.01)}`
        : share.format(value),
    /** Percent points, signed: 2.7 -> "+2.7%". */
    signedPct: (points: number) => signed.format(points / 100),
  };
};

export type ReportFormat = ReturnType<typeof createReportFormat>;

/**
 * The Luntero search for one group of a report (a property type, a
 * furnishing, a neighbourhood), in the report's city when it has one. `city`
 * is the canonical city name the search filters on.
 */
export const reportSearchHref = (filters: {
  city?: string | null;
  neighborhood?: string;
  type?: string;
  furnished?: string;
}) => {
  const params = new URLSearchParams();
  if (filters.city) params.set("city", filters.city);
  if (filters.neighborhood) params.set("neighborhood", filters.neighborhood);
  if (filters.type) params.set("type", filters.type);
  if (filters.furnished) params.set("furnished", filters.furnished);
  return `/search?${params.toString()}`;
};

export type ChangeTone = "up" | "down" | "flat";

export const changeToneOf = (points: number | null): ChangeTone | null => {
  if (points === null) return null;
  if (Math.abs(points) < 0.05) return "flat";
  return points > 0 ? "up" : "down";
};

/**
 * Change is diverging: a rise reads warm (orange), a fall cool (blue), no
 * change neutral grey. The pair is colour-blind safe; the sign and arrow carry
 * the direction as well, so colour is never the only cue.
 */
export const CHANGE_TONE_CLASSES: Record<ChangeTone, string> = {
  up: "bg-orange-50 text-orange-800",
  down: "bg-blue-50 text-blue-800",
  flat: "bg-zinc-100 text-zinc-700",
};

export const CHANGE_BAR_CLASSES: Record<ChangeTone, string> = {
  up: "bg-orange-600",
  down: "bg-blue-600",
  flat: "bg-zinc-400",
};

/** One-hue ordinal ramp for ordered bands (cheapest to most expensive). */
export const BAND_RAMP = [
  "bg-blue-400",
  "bg-blue-500",
  "bg-blue-600",
  "bg-blue-800",
  "bg-blue-950",
] as const;

/** Single-series marks: the series colour, and the emphasised entry. */
export const SERIES_CLASS = "bg-blue-300";
export const EMPHASIS_CLASS = "bg-blue-700";

/** How a band or bin is worded, from the page's translations. */
export type BandLabeler = {
  under: (value: string) => string;
  upTo: (value: string) => string;
  over: (value: string) => string;
  andUp: (value: string) => string;
  range: (from: string, to: string) => string;
};

/**
 * Label for a computed band key: `under-1000`, `up-to-1000`, `over-2500`,
 * `2500-plus` or `1000-1499`. The keys are stable language-neutral ids; the
 * wording and number format come from the visitor's language.
 */
export const bandLabel = (
  key: string,
  format: (value: number) => string,
  labeler: BandLabeler,
) => {
  const under = key.match(/^under-(\d+)$/);
  if (under) return labeler.under(format(Number(under[1])));
  const upTo = key.match(/^up-to-(\d+)$/);
  if (upTo) return labeler.upTo(format(Number(upTo[1])));
  const over = key.match(/^over-(\d+)$/);
  if (over) return labeler.over(format(Number(over[1])));
  const plus = key.match(/^(\d+)-plus$/);
  if (plus) return labeler.andUp(format(Number(plus[1])));
  const range = key.match(/^(\d+)-(\d+)$/);
  if (range)
    return labeler.range(format(Number(range[1])), format(Number(range[2])));
  return key;
};

/** Label for one histogram bin `[from, to)`; `step` is the smallest unit. */
export const binLabel = (
  bin: ReportBin,
  format: (value: number) => string,
  labeler: BandLabeler,
  step = 1,
) => {
  if (bin.from === 0 && bin.to !== null) return labeler.under(format(bin.to));
  if (bin.to === null) return labeler.andUp(format(bin.from));
  return labeler.range(format(bin.from), format(bin.to - step));
};

/** Index of the bin that holds `value`. */
export const binIndexOf = (bins: ReportBin[], value: number) =>
  bins.findIndex(
    (bin) => value >= bin.from && (bin.to === null || value < bin.to),
  );

/** Short axis label for a bin: its lower edge, "<500" first, "3,000+" last. */
export const binTick = (bin: ReportBin, format: (value: number) => string) =>
  bin.from === 0 && bin.to !== null
    ? `<${format(bin.to)}`
    : bin.to === null
      ? `${format(bin.from)}+`
      : format(bin.from);

/**
 * Where `value` falls along a row of equal-width histogram columns, 0 to 1.
 * The open last bin is treated as wide as the one before it.
 */
export const binPositionOf = (bins: ReportBin[], value: number) => {
  const index = binIndexOf(bins, value);
  if (index < 0) return value <= (bins[0]?.from ?? 0) ? 0 : 1;
  const bin = bins[index];
  const previous = bins[index - 1];
  const to =
    bin.to ?? bin.from + (previous ? bin.from - previous.from : bin.from || 1);
  const fraction = Math.min(
    1,
    Math.max(0, (value - bin.from) / (to - bin.from)),
  );
  return (index + fraction) / bins.length;
};

/** Section eyebrow: the running section number, then the label. */
export const SECTION_EYEBROW_CLASS =
  "flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground before:rounded-md before:bg-secondary before:px-1.5 before:py-0.5 before:tabular-nums before:text-secondary-foreground before:content-[counter(report-section,decimal-leading-zero)]";

/** Every numbered report section increments the running counter. */
export const SECTION_CLASS =
  "flex scroll-mt-24 flex-col gap-6 [counter-increment:report-section] sm:gap-8";
